import isBuilderContext from "@/lib/is-platform/is-builder-context"
import debounce from "@/lib/debounce"

// https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore#_isempty
export const isEmpty = <T>(
  obj: T | null | undefined
): obj is null | undefined =>
  obj === null ||
  obj === undefined ||
  obj === "" ||
  (typeof obj === "object" &&
    !(obj instanceof Date) &&
    obj !== null &&
    !Object.entries(obj).length)

export const isPresent = <T>(obj: T | null | undefined): obj is T =>
  !isEmpty(obj)

export const isBoolean = (bool: any): bool is boolean =>
  typeof bool === "boolean"

export const isNumber = (num: any): num is number =>
  (typeof num === "number" || (typeof num === "string" && num.trim() !== "")) &&
  !isNaN(num as number)

//https://stackoverflow.com/a/33121880
export const unique = <T>(array: T[]): T[] => [...new Set(array)]

export const getImageUrl = (
  image: any,
  defaultUrl: string | null = null
): string | null =>
  !isEmpty(image) && !isEmpty(image.image) ? image.image.url : defaultUrl

export const generateSrcset = (sizes: any): string | null => {
  if (!sizes || !sizes.base_url || !sizes.data) {
    return null
  }

  const sizesData = Object.values(sizes.data) as any[]
  if (sizesData.length === 0) {
    return null
  }

  // Determine the query separator: use ? if base_url has no query string, otherwise use &
  const separator = sizes.base_url.includes("?") ? "&" : "?"

  return sizesData
    .sort((a: any, b: any) => a.w - b.w)
    .map((size: any) => {
      let url = `${sizes.base_url}${separator}w=${size.w}`
      if (size.h) {
        url += `&h=${size.h}`
      }
      return `${url}&s=${size.s} ${size.w}w`
    })
    .join(", ")
}

export const capitalizeFirstLetter = (str: string): string =>
  str.charAt(0).toUpperCase() + str.slice(1)

// https://youmightnotneed.com/lodash/#omit
export const omit = (
  object: { [key: string]: any },
  keysToRemove: string[]
): { [key: string]: any } => {
  const obj = { ...object }
  keysToRemove.forEach(key => delete obj[key])
  return obj
}

export const isObject = (obj: any): obj is object =>
  typeof obj === "object" && obj !== null

export const clientWidth = (): number =>
  window.innerWidth ||
  document.documentElement.clientWidth ||
  document.body.clientWidth

export const clientHeight = (): number =>
  window.innerHeight ||
  document.documentElement.clientHeight ||
  document.body.clientHeight

export const getHostName = (url: string): string => {
  const parser = document.createElement("a")
  parser.href = url

  if (parser.hostname === "localhost") {
    return url
  } else {
    return parser.hostname
  }
}

export const isString = (str: any): str is string => typeof str === "string"

export const pluralize = (string: string, amount = 2): string => {
  if (!string) return string

  // Remove hyphens and spaces when checking for Latin-only characters
  const hasNonLatinCharacters = !/^[A-Za-z]+$/.test(
    string.replaceAll("-", "").replaceAll(" ", "")
  )

  if (hasNonLatinCharacters) return string

  // Apply standard pluralization for Latin strings
  return amount > 1 && !string.endsWith("s") ? `${string}s` : string
}

export const getInitials = (string: string): string => {
  if (!isString(string)) return ""

  const SIMPLE_EMAIL_REGEX = /^\S+@\S+\.\S+$/

  if (string.match(SIMPLE_EMAIL_REGEX)) {
    return string.slice(0, 2).toUpperCase()
  } else {
    return string
      .replace(/[^a-zA-Z- ]/g, " ")
      .replace(/  +/g, " ")
      .split(" ")
      .slice(0, 2)
      .map(s => s.charAt(0))
      .join("")
      .toUpperCase()
  }
}

export const hasKey = (obj: any, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key)

export const isWebLink = (link: string): boolean =>
  !!link &&
  (link.startsWith("http") ||
    link.startsWith("mailto") ||
    link.startsWith("tel"))

export const replaceQueryParam = (router: any, param: any): void =>
  router.replace({ query: { ...router.currentRoute.query, ...param } })

export const eventPath = (event: Event): EventTarget[] =>
  event.composedPath && event.composedPath()

export const findAsync = async <T>(
  arr: T[],
  asyncCallback: (item: any) => Promise<boolean>
): Promise<T | undefined> => {
  const promises = arr.map(asyncCallback)
  const results = await Promise.all(promises)
  const index = results.findIndex(result => result)
  return arr[index]
}

export const formatSlug = (slug: string): string =>
  slug
    .split("-")
    .map(capitalizeFirstLetter)
    .join(" ")

export const getParentElementsBySelector = (
  node: Element,
  selector: string,
  includeSelf = false,
  acc: Element[] = []
): Element[] => {
  if (includeSelf && node.matches(selector)) {
    acc.push(node)
  }

  if (node.parentElement) {
    return getParentElementsBySelector(node.parentElement, selector, true, acc)
  }

  return acc
}

export const getSafeAreaInsetTop = (): number => {
  const safeAreaInsetTop = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--safe-area-inset-top")

  return parseInt(safeAreaInsetTop, 10) || 0
}

/**
 * Watches an element for resize changes using ResizeObserver.
 *
 * Per the ResizeObserver specification: "Observation will fire when observation
 * starts if Element is being rendered, and Element's size is not 0,0."
 *
 * This means the callback fires immediately with the initial size, then on subsequent resizes.
 *
 * @param targetRef - Element or function returning an element to observe
 * @param cb - Callback receiving { height, width } dimensions
 * @param options.debounce - Debounce delay for subsequent callbacks (first callback is immediate)
 * @param options.maxRetries - Max retries to find element if not available immediately
 *
 * @see https://drafts.csswg.org/resize-observer/
 */
export const observeResize = (
  targetRef: (() => Element | null) | Element | null,
  cb: (dimensions: { height: number; width: number }) => void,
  options?: {
    debounce?: number
    counter?: number
    maxRetries?: number
  }
): { disconnect: () => void } => {
  const counter = options?.counter ?? 1
  const maxRetries = options?.maxRetries ?? 10
  const debounceDelay = options?.debounce ?? 0

  const target = typeof targetRef === "function" ? targetRef() : targetRef

  if (!target) {
    if (counter < maxRetries) {
      let futureObserver: { disconnect: () => void } | null = null
      const timer = setTimeout(() => {
        futureObserver = observeResize(targetRef, cb, {
          ...options,
          counter: counter + 1,
          maxRetries
        })
      }, 100)
      return {
        disconnect: () => {
          clearTimeout(timer)
          if (futureObserver) {
            futureObserver.disconnect()
          }
        }
      }
    }

    return {
      disconnect: () => {}
    }
  }

  // Track if this is the first callback (ResizeObserver fires immediately on observe)
  let isFirstCallback = true

  // Store the latest dimensions for the debounced callback
  let latestDimensions: { height: number; width: number } | null = null

  // Create a stable callback function for debouncing
  const debouncedCallback = function() {
    if (latestDimensions) {
      cb(latestDimensions)
    }
  }

  const handleResize = (dimensions: { height: number; width: number }) => {
    // First callback: always fire immediately (no debounce)
    if (isFirstCallback) {
      isFirstCallback = false
      cb(dimensions)
      return
    }

    // Subsequent callbacks: apply debounce if specified
    if (debounceDelay > 0) {
      latestDimensions = dimensions
      debounce(debouncedCallback, debounceDelay)
    } else {
      cb(dimensions)
    }
  }

  const observer = new ResizeObserver(entries => {
    const entry = entries.find(e => e.target === target)
    // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverEntry/contentRect
    if (entry) {
      const htmlTarget = target as HTMLElement
      const height =
        (entry?.borderBoxSize && entry.borderBoxSize[0]
          ? entry.borderBoxSize[0].blockSize
          : undefined) ||
        entry?.contentRect?.height ||
        htmlTarget?.offsetHeight ||
        0
      const width =
        (entry?.borderBoxSize && entry.borderBoxSize[0]
          ? entry.borderBoxSize[0].inlineSize
          : undefined) ||
        entry?.contentRect?.width ||
        htmlTarget?.offsetWidth ||
        0
      handleResize({ height, width })
    }
  })
  observer.observe(target)

  return {
    disconnect: () => {
      if ((debouncedCallback as any)._tId) {
        clearTimeout((debouncedCallback as any)._tId)
      }
      observer.disconnect()
    }
  }
}

export { isBuilderContext }
